import { friendlyDbError } from '../utils/friendlyDbError';
/**
 * Lead Pipeline Store
 *
 * Store Zustand per gestire le pipeline dei lead e i loro stage.
 * Gestisce CRUD completo, riordinamento, e sincronizzazione con Supabase.
 */

import { create } from 'zustand';
import { supabase } from '../supabase';
import { usePermissionsStore } from './permissionsStore';
import { isPipelineVisible } from '../utils/leadVisibility';
import { getCurrentUserOrganizationId, getCachedClientStates } from './referenceDataCache';
import { LeadPipeline, LeadPipelineStage, LeadPipelineFormData, LeadPipelineStageFormData } from '../types/leadPipeline';
import { sortByStageOrder, nextStageSortOrder } from '../utils/pipelineStageOrder';
import { useSettingsStore } from './settingsStore';
import { dispatchTaskCompleted } from '../onboarding/dispatchTaskCompleted';

// Helper per ottenere la label dinamica del lead (singolare)
function getLeadLabelSingular(): string {
  const settings = useSettingsStore.getState().settings;
  return settings?.menu_settings?.menu_labels?.leads_label_singular || 'Lead';
}

// Per tracciare l'organizzazione e resettare lo stato quando cambia
let lastFetchedOrganizationId: string | null = null;

// Timestamp ultima fetch delle fasi per pipeline: fetchPipelines le carica già
// tutte in embed, evita che il mount della pagina rifaccia subito la stessa query
const stagesFetchedAt = new Map<string, number>();
const STAGES_FRESH_MS = 5000;

// Lanciato da deletePipeline quando ci sono lead/clienti assegnati:
// permette alla UI di offrire una risoluzione (sposta/scollega) invece del solo errore.
export class PipelineHasAssignmentsError extends Error {
  total: number;

  constructor(total: number) {
    super(`Impossibile eliminare: ${total} lead/clienti sono assegnati a questa pipeline`);
    this.name = 'PipelineHasAssignmentsError';
    this.total = total;
  }
}

export type PipelineDeletionResolution =
  | { type: 'move'; targetPipelineId: string; targetStageId: string }
  | { type: 'unassign' };

interface LeadPipelineStore {
  // State
  pipelines: LeadPipeline[];
  stages: Map<string, LeadPipelineStage[]>; // pipelineId -> stages[]
  selectedPipelineId: string | null;
  /**
   * Sottoinsieme di pipeline da aggregare quando selectedPipelineId === 'all'.
   * null = aggrega TUTTE le pipeline visibili (comportamento storico della voce
   * "Tutte le pipeline"); array = aggrega solo quelle spuntate nel selettore
   * multi-selezione. Ignorato quando la selezione è una singola pipeline.
   */
  aggregatePipelineIds: string[] | null;
  loading: boolean;
  error: string | null;
  hasLegacyLeads: boolean; // true se ci sono lead senza pipeline_id

  // Pipeline Operations
  fetchPipelines: () => Promise<void>;
  createPipeline: (data: LeadPipelineFormData) => Promise<LeadPipeline | null>;
  updatePipeline: (id: string, updates: Partial<LeadPipeline>) => Promise<LeadPipeline | null>;
  deletePipeline: (id: string) => Promise<void>;
  countPipelineAssignments: (id: string) => Promise<{ primary: number; extra: number; total: number }>;
  resolveAndDeletePipeline: (id: string, resolution: PipelineDeletionResolution) => Promise<void>;
  setDefaultPipeline: (id: string) => Promise<void>;
  reorderPipelines: (pipelines: LeadPipeline[]) => Promise<void>;

  // Stage Operations
  fetchStagesByPipeline: (pipelineId: string) => Promise<void>;
  createStage: (pipelineId: string, data: Omit<LeadPipelineStageFormData, 'pipeline_id'>) => Promise<LeadPipelineStage | null>;
  updateStage: (id: string, updates: Partial<LeadPipelineStage>) => Promise<LeadPipelineStage | null>;
  deleteStage: (id: string) => Promise<void>;
  reorderStages: (pipelineId: string, stages: LeadPipelineStage[]) => Promise<void>;

  // UI State
  setSelectedPipeline: (id: string | null) => void;
  setAggregatePipelineIds: (ids: string[] | null) => void;

  // Utility
  getDefaultPipeline: () => LeadPipeline | null;
  getStagesForPipeline: (pipelineId: string) => LeadPipelineStage[];
  getPipelineById: (id: string) => LeadPipeline | null;

  // Legacy pipeline check
  checkHasLegacyLeads: () => Promise<void>;
}

export const useLeadPipelineStore = create<LeadPipelineStore>((set, get) => ({
  // Initial State
  pipelines: [],
  stages: new Map(),
  selectedPipelineId: null,
  aggregatePipelineIds: null,
  loading: false,
  error: null,
  hasLegacyLeads: false,

  // =====================================================
  // PIPELINE OPERATIONS
  // =====================================================

  fetchPipelines: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization ID from cache
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) throw new Error('No organization');

      // Se l'organizzazione è cambiata, resetta lo stato
      if (lastFetchedOrganizationId && lastFetchedOrganizationId !== organizationId) {
        console.log('[LeadPipelineStore] Organization changed, resetting state');
        set({ selectedPipelineId: null, aggregatePipelineIds: null, pipelines: [], stages: new Map() });
      }
      lastFetchedOrganizationId = organizationId;

      // Embed delle fasi: una query sola invece della catena seriale
      // pipeline → fasi (il kanban può partire un round-trip prima)
      const { data: rawData, error } = await supabase
        .from('lead_pipelines')
        .select('*, stages:lead_pipeline_stages(*)')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })
        .order('id', { ascending: true });

      if (error) throw error;

      // Popola subito la mappa delle fasi (solo attive, ordine stabile)
      const data = (rawData || []).map(({ stages: embeddedStages, ...pipeline }: any) => {
        set(state => {
          const newStages = new Map(state.stages);
          newStages.set(pipeline.id, sortByStageOrder((embeddedStages || []).filter((s: any) => s.is_active)));
          return { stages: newStages };
        });
        stagesFetchedAt.set(pipeline.id, Date.now());
        return pipeline;
      });

      // Crea pipeline virtuale "legacy" per lead senza pipeline assegnata
      const leadLabel = getLeadLabelSingular();
      const legacyPipeline: LeadPipeline = {
        id: 'legacy',
        organization_id: organizationId,
        // Mai "predefinito" nel nome: e' l'opposto della pipeline predefinita, ed e'
        // proprio la parola che faceva scegliere questa voce per sbaglio.
        name: 'Senza pipeline',
        description: `${leadLabel} fuori da ogni pipeline: non compaiono nelle altre viste`,
        color: '#6B7280', // gray-500
        is_default: false,
        is_active: true,
        sort_order: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: undefined
      };

      // Aggiungi pipeline legacy come prima opzione, seguita dalle pipeline custom
      // Restrizioni di visibilità del ruolo: solo le pipeline consentite; con la
      // restrizione attiva la "legacy" (lead senza pipeline) non si mostra.
      const leadVisibility = usePermissionsStore.getState().getLeadVisibility();
      const visibleData = (data || []).filter((p: any) => isPipelineVisible(p.id, leadVisibility));
      const allPipelines = leadVisibility?.pipelineIds ? visibleData : [legacyPipeline, ...visibleData];
      set({ pipelines: allPipelines });

      // Auto-select pipeline:
      // 1. Riprendi da localStorage se presente
      // 2. Altrimenti seleziona la pipeline di default
      // 3. Se non ci sono pipeline, seleziona 'legacy'
      const currentSelected = get().selectedPipelineId;
      const savedPipelineId = localStorage.getItem(`selectedLeadPipelineId_org_${organizationId}`);

      // Riprendi anche il sottoinsieme aggregato salvato (vista multi-pipeline)
      try {
        const savedAgg = localStorage.getItem(`aggregateLeadPipelineIds_org_${organizationId}`);
        if (savedAgg) {
          const parsed = JSON.parse(savedAgg);
          if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string')) {
            set({ aggregatePipelineIds: parsed.length > 0 ? parsed : null });
          }
        }
      } catch (_) { /* JSON invalido: ignora, resta null */ }

      if (!currentSelected) {
        if (savedPipelineId && ((savedPipelineId === 'legacy' && !leadVisibility?.pipelineIds) || savedPipelineId === 'all' || visibleData.some(p => p.id === savedPipelineId))) {
          console.log('[LeadPipelineStore] fetchPipelines: selecting saved pipeline:', savedPipelineId);
          get().setSelectedPipeline(savedPipelineId);
        } else {
          // Seleziona la pipeline di default o la prima disponibile, o 'legacy' se non ce ne sono
          const defaultPipeline = visibleData.find(p => p.is_default) || visibleData[0];
          if (defaultPipeline) {
            console.log('[LeadPipelineStore] fetchPipelines: selecting default pipeline:', defaultPipeline.id);
            get().setSelectedPipeline(defaultPipeline.id);
          } else {
            // Nessuna pipeline disponibile, seleziona 'legacy'
            console.log('[LeadPipelineStore] fetchPipelines: selecting legacy pipeline');
            get().setSelectedPipeline('legacy');
          }
        }
      }
    } catch (error) {
      console.error('[LeadPipelineStore] Error fetching pipelines:', error);
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createPipeline: async (pipelineData: LeadPipelineFormData) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('No organization');

      // Una sola pipeline default per org: se la nuova nasce default, disattivo le altre prima
      if (pipelineData.is_default) {
        await supabase
          .from('lead_pipelines')
          .update({ is_default: false })
          .eq('organization_id', profile.organization_id);
      }

      const { data, error } = await supabase
        .from('lead_pipelines')
        .insert({
          ...pipelineData,
          organization_id: profile.organization_id,
          created_by: userData.user.id
        })
        .select()
        .single();

      if (error) throw error;

      // Aggiungi alla lista locale (e disattiva il default sulle altre se questa lo è)
      set(state => ({
        pipelines: [...state.pipelines.map(p => pipelineData.is_default ? { ...p, is_default: false } : p), data]
          .sort((a, b) => a.sort_order - b.sort_order)
      }));

      dispatchTaskCompleted('pipeline', 'pipeline_store_create');

      return data;
    } catch (error) {
      console.error('[LeadPipelineStore] Error creating pipeline:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updatePipeline: async (id: string, updates: Partial<LeadPipeline>) => {
    try {
      // Una sola pipeline default per org: se questa diventa default, disattivo le altre prima
      if (updates.is_default) {
        const organizationId = get().pipelines.find(p => p.id === id)?.organization_id;
        if (organizationId) {
          await supabase
            .from('lead_pipelines')
            .update({ is_default: false })
            .eq('organization_id', organizationId)
            .neq('id', id);
        }
      }

      const { data, error } = await supabase
        .from('lead_pipelines')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Aggiorna lo stato locale
      set(state => ({
        pipelines: state.pipelines.map(p => {
          if (p.id === id) return data;
          return updates.is_default ? { ...p, is_default: false } : p;
        })
      }));

      dispatchTaskCompleted('pipeline', 'pipeline_store_update');

      return data;
    } catch (error) {
      console.error('[LeadPipelineStore] Error updating pipeline:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  countPipelineAssignments: async (id: string) => {
    // Controlla se ci sono lead assegnati (primari)
    const { count: primaryCount, error: countError } = await supabase
      .from('clients')
      .select('id', { count: 'exact', head: true })
      .eq('pipeline_id', id);

    if (countError) throw countError;

    // Controlla anche membership extra nella junction table
    const { count: extraCount, error: extraCountError } = await supabase
      .from('client_pipeline_memberships')
      .select('id', { count: 'exact', head: true })
      .eq('pipeline_id', id);

    if (extraCountError) throw extraCountError;

    const primary = primaryCount || 0;
    const extra = extraCount || 0;
    return { primary, extra, total: primary + extra };
  },

  deletePipeline: async (id: string) => {
    try {
      const { total } = await get().countPipelineAssignments(id);
      if (total > 0) {
        throw new PipelineHasAssignmentsError(total);
      }

      // Bonifica riferimenti orfani: clienti la cui pipeline_stage_id punta a uno
      // stage di questa pipeline pur avendo pipeline_id diverso (o NULL). Non vengono
      // contati come assegnati ma bloccherebbero il DELETE via FK
      // clients_pipeline_stage_id_fkey quando la cancellazione cascata sugli stage.
      const { data: stageRows, error: stagesError } = await supabase
        .from('lead_pipeline_stages')
        .select('id')
        .eq('pipeline_id', id);

      if (stagesError) throw stagesError;

      const stageIds = (stageRows || []).map(s => s.id);
      if (stageIds.length > 0) {
        const { error: orphanClientsError } = await supabase
          .from('clients')
          .update({ pipeline_stage_id: null })
          .in('pipeline_stage_id', stageIds);

        if (orphanClientsError) throw orphanClientsError;

        // Stessa bonifica per membership incoerenti (stage di questa pipeline ma
        // pipeline_id diverso): pipeline_stage_id è NOT NULL, quindi si eliminano.
        const { error: orphanMembershipsError } = await supabase
          .from('client_pipeline_memberships')
          .delete()
          .in('pipeline_stage_id', stageIds);

        if (orphanMembershipsError) throw orphanMembershipsError;
      }

      const { error } = await supabase
        .from('lead_pipelines')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Rimuovi dalla lista locale e dagli stage
      set(state => {
        const newStages = new Map(state.stages);
        newStages.delete(id);

        return {
          pipelines: state.pipelines.filter(p => p.id !== id),
          stages: newStages
        };
      });

      // Se era la pipeline selezionata, seleziona la default
      if (get().selectedPipelineId === id) {
        const defaultPipeline = get().getDefaultPipeline();
        get().setSelectedPipeline(defaultPipeline?.id || null);
      }
    } catch (error) {
      console.error('[LeadPipelineStore] Error deleting pipeline:', error);
      // L'errore di "assegnazioni presenti" viene gestito dalla UI (offre la risoluzione),
      // non deve restare visualizzato come banner di errore permanente dello store.
      if (!(error instanceof PipelineHasAssignmentsError)) {
        set({ error: friendlyDbError(error) });
      }
      throw error; // Re-throw per permettere al chiamante di gestire l'errore
    }
  },

  resolveAndDeletePipeline: async (sourceId: string, resolution: PipelineDeletionResolution) => {
    try {
      if (resolution.type === 'move') {
        const { targetPipelineId, targetStageId } = resolution;

        // 1a. Sposta i clienti/lead primari sulla pipeline+stage di destinazione
        const { data: movedClients, error: moveClientsError } = await supabase
          .from('clients')
          .update({ pipeline_id: targetPipelineId, pipeline_stage_id: targetStageId })
          .eq('pipeline_id', sourceId)
          .select('id');

        if (moveClientsError) throw moveClientsError;

        // 1b. Cleanup coerenza: evita che un cliente appena diventato primario sulla
        // pipeline target abbia anche una membership extra sulla stessa pipeline
        const movedClientIds = (movedClients || []).map(c => c.id);
        if (movedClientIds.length > 0) {
          const { error: cleanupError } = await supabase
            .from('client_pipeline_memberships')
            .delete()
            .eq('pipeline_id', targetPipelineId)
            .in('client_id', movedClientIds);

          if (cleanupError) throw cleanupError;
        }

        // 1c. Sposta le membership extra rimanenti sulla pipeline sorgente.
        // Il vincolo UNIQUE(client_id, pipeline_id) impedisce l'update per i clienti
        // che hanno già una membership sulla pipeline target: per quelli si cancella
        // la riga sorgente (il cliente resta comunque assegnato tramite quella già esistente).
        const { data: existingTargetMemberships, error: existingTargetError } = await supabase
          .from('client_pipeline_memberships')
          .select('client_id')
          .eq('pipeline_id', targetPipelineId);

        if (existingTargetError) throw existingTargetError;

        const clientsAlreadyOnTarget = (existingTargetMemberships || []).map(m => m.client_id);

        if (clientsAlreadyOnTarget.length > 0) {
          const { error: deleteConflictError } = await supabase
            .from('client_pipeline_memberships')
            .delete()
            .eq('pipeline_id', sourceId)
            .in('client_id', clientsAlreadyOnTarget);

          if (deleteConflictError) throw deleteConflictError;
        }

        const { error: moveMembershipsError } = await supabase
          .from('client_pipeline_memberships')
          .update({ pipeline_id: targetPipelineId, pipeline_stage_id: targetStageId })
          .eq('pipeline_id', sourceId);

        if (moveMembershipsError) throw moveMembershipsError;
      } else {
        // Scollega: il cliente resta in clients, si azzera solo il legame con questa pipeline
        const { error: unassignError } = await supabase
          .from('clients')
          .update({ pipeline_id: null, pipeline_stage_id: null })
          .eq('pipeline_id', sourceId);

        if (unassignError) throw unassignError;

        const { error: deleteMembershipsError } = await supabase
          .from('client_pipeline_memberships')
          .delete()
          .eq('pipeline_id', sourceId);

        if (deleteMembershipsError) throw deleteMembershipsError;
      }

      // A questo punto il conteggio è 0: procede con la cancellazione vera e propria
      await get().deletePipeline(sourceId);
    } catch (error) {
      console.error('[LeadPipelineStore] Error resolving and deleting pipeline:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  setDefaultPipeline: async (id: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('No organization');

      // 1. Reset tutte le pipeline a non-default
      await supabase
        .from('lead_pipelines')
        .update({ is_default: false })
        .eq('organization_id', profile.organization_id);

      // 2. Imposta quella selezionata come default
      const { error } = await supabase
        .from('lead_pipelines')
        .update({ is_default: true })
        .eq('id', id);

      if (error) throw error;

      // 3. Refresh la lista
      await get().fetchPipelines();

    } catch (error) {
      console.error('[LeadPipelineStore] Error setting default pipeline:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  reorderPipelines: async (orderedPipelines: LeadPipeline[]) => {
    try {
      // Update sort_order per ogni pipeline
      const updates = orderedPipelines.map((pipeline, index) =>
        supabase
          .from('lead_pipelines')
          .update({ sort_order: index })
          .eq('id', pipeline.id)
      );

      await Promise.all(updates);

      // Aggiorna lo stato locale
      set({ pipelines: orderedPipelines });

    } catch (error) {
      console.error('[LeadPipelineStore] Error reordering pipelines:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  // =====================================================
  // STAGE OPERATIONS
  // =====================================================

  fetchStagesByPipeline: async (pipelineId: string) => {
    console.log('[LeadPipelineStore] fetchStagesByPipeline called:', pipelineId);
    // Guard: prevent fetching stages for the virtual 'legacy' pipeline
    if (pipelineId === 'legacy' || !pipelineId) {
      console.log('[LeadPipelineStore] fetchStagesByPipeline: skipping (legacy or empty)');
      return;
    }

    // Le fasi arrivano già dall'embed di fetchPipelines: se sono fresche
    // evita di rifare subito la stessa query al mount della pagina
    const fetchedAt = stagesFetchedAt.get(pipelineId);
    if (fetchedAt && Date.now() - fetchedAt < STAGES_FRESH_MS && get().stages.has(pipelineId)) {
      return;
    }

    try {
      const { data, error } = await supabase
        .from('lead_pipeline_stages')
        .select('*')
        .eq('pipeline_id', pipelineId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })
        .order('id', { ascending: true });

      if (error) throw error;

      console.log('[LeadPipelineStore] fetchStagesByPipeline completed:', pipelineId, 'stages count:', data?.length);

      // Aggiorna la mappa degli stage.
      // sortByStageOrder garantisce ordine stabile anche con sort_order duplicati
      // (Postgres non garantisce l'ordine a parità di valore).
      set(state => {
        const newStages = new Map(state.stages);
        newStages.set(pipelineId, sortByStageOrder(data || []));
        console.log('[LeadPipelineStore] fetchStagesByPipeline: updated stages map, new size:', newStages.size);
        return { stages: newStages };
      });
      stagesFetchedAt.set(pipelineId, Date.now());
    } catch (error) {
      console.error('[LeadPipelineStore] Error fetching stages:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  createStage: async (pipelineId: string, stageData: Omit<LeadPipelineStageFormData, 'pipeline_id'>) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('No organization');

      // Se il chiamante non specifica sort_order, assegna max esistente + 1:
      // stage tutti a 0 rendono l'ordine del Kanban non deterministico.
      let sortOrder = stageData.sort_order;
      if (sortOrder == null) {
        if (!get().stages.has(pipelineId)) {
          await get().fetchStagesByPipeline(pipelineId);
        }
        sortOrder = nextStageSortOrder(get().getStagesForPipeline(pipelineId));
      }

      const { data, error } = await supabase
        .from('lead_pipeline_stages')
        .insert({
          ...stageData,
          sort_order: sortOrder,
          pipeline_id: pipelineId,
          organization_id: profile.organization_id
        })
        .select()
        .single();

      if (error) throw error;

      // Aggiorna la mappa degli stage
      set(state => {
        const newStages = new Map(state.stages);
        const currentStages = newStages.get(pipelineId) || [];
        newStages.set(
          pipelineId,
          sortByStageOrder([...currentStages, data])
        );
        return { stages: newStages };
      });

      dispatchTaskCompleted('pipeline', 'pipeline_store_stage_create');

      return data;
    } catch (error) {
      console.error('[LeadPipelineStore] Error creating stage:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateStage: async (id: string, updates: Partial<LeadPipelineStage>) => {
    try {
      const { data, error } = await supabase
        .from('lead_pipeline_stages')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Aggiorna la mappa degli stage
      set(state => {
        const newStages = new Map(state.stages);
        const pipelineId = data.pipeline_id;
        const currentStages = newStages.get(pipelineId) || [];
        newStages.set(
          pipelineId,
          currentStages.map(s => s.id === id ? data : s)
        );
        return { stages: newStages };
      });

      dispatchTaskCompleted('pipeline', 'pipeline_store_stage_update');

      return data;
    } catch (error) {
      console.error('[LeadPipelineStore] Error updating stage:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteStage: async (id: string) => {
    try {
      // Controlla se ci sono lead primari in questo stage
      const { count: primaryCount, error: countError } = await supabase
        .from('clients')
        .select('id', { count: 'exact', head: true })
        .eq('pipeline_stage_id', id);

      if (countError) throw countError;

      // Controlla anche membership extra nella junction table
      const { count: extraCount, error: extraCountError } = await supabase
        .from('client_pipeline_memberships')
        .select('id', { count: 'exact', head: true })
        .eq('pipeline_stage_id', id);

      if (extraCountError) throw extraCountError;

      const totalCount = (primaryCount || 0) + (extraCount || 0);
      if (totalCount > 0) {
        throw new Error(`Impossibile eliminare: ${totalCount} lead/clienti sono in questo stage`);
      }

      const { error } = await supabase
        .from('lead_pipeline_stages')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Rimuovi dalla mappa degli stage
      set(state => {
        const newStages = new Map();
        state.stages.forEach((stages, pipelineId) => {
          newStages.set(pipelineId, stages.filter(s => s.id !== id));
        });
        return { stages: newStages };
      });

    } catch (error) {
      console.error('[LeadPipelineStore] Error deleting stage:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  reorderStages: async (pipelineId: string, orderedStages: LeadPipelineStage[]) => {
    try {
      // Update sort_order per ogni stage
      const updates = orderedStages.map((stage, index) =>
        supabase
          .from('lead_pipeline_stages')
          .update({ sort_order: index })
          .eq('id', stage.id)
      );

      await Promise.all(updates);

      // Aggiorna la mappa locale
      set(state => {
        const newStages = new Map(state.stages);
        newStages.set(pipelineId, orderedStages);
        return { stages: newStages };
      });
    } catch (error) {
      console.error('[LeadPipelineStore] Error reordering stages:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  // =====================================================
  // UI STATE
  // =====================================================

  setSelectedPipeline: (id: string | null) => {
    console.log('[LeadPipelineStore] setSelectedPipeline called:', id);
    set({ selectedPipelineId: id });

    // Salva in localStorage per persistenza (con chiave org-specific)
    const storageKey = lastFetchedOrganizationId
      ? `selectedLeadPipelineId_org_${lastFetchedOrganizationId}`
      : 'selectedLeadPipelineId';

    if (id) {
      localStorage.setItem(storageKey, id);
      // Fetch automaticamente gli stage di questa pipeline SOLO se non già in cache
      // Skip fetching for legacy pipeline
      const stages = get().stages;
      const hasStages = stages.has(id);
      console.log('[LeadPipelineStore] setSelectedPipeline: hasStages in cache?', hasStages, 'stagesMapSize:', stages.size);
      // 'all' = vista aggregata: gli stage di tutte le pipeline sono già
      // embeddati da fetchPipelines, niente fetch per-pipeline
      if (!hasStages && id !== 'legacy' && id !== 'all') {
        console.log('[LeadPipelineStore] setSelectedPipeline: fetching stages for', id);
        get().fetchStagesByPipeline(id);
      }
    } else {
      localStorage.removeItem(storageKey);
    }
  },

  setAggregatePipelineIds: (ids: string[] | null) => {
    const normalized = ids && ids.length > 0 ? ids : null;
    set({ aggregatePipelineIds: normalized });
    const storageKey = lastFetchedOrganizationId
      ? `aggregateLeadPipelineIds_org_${lastFetchedOrganizationId}`
      : 'aggregateLeadPipelineIds';
    if (normalized) {
      localStorage.setItem(storageKey, JSON.stringify(normalized));
    } else {
      localStorage.removeItem(storageKey);
    }
  },

  // =====================================================
  // UTILITY FUNCTIONS
  // =====================================================

  getDefaultPipeline: () => {
    const { pipelines } = get();
    const realPipelines = pipelines.filter(p => p.id !== 'legacy');
    return realPipelines.find(p => p.is_default) || realPipelines[0] || null;
  },

  getStagesForPipeline: (pipelineId: string) => {
    return get().stages.get(pipelineId) || [];
  },

  getPipelineById: (id: string) => {
    return get().pipelines.find(p => p.id === id) || null;
  },

  // =====================================================
  // LEGACY PIPELINE CHECK
  // =====================================================

  checkHasLegacyLeads: async () => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      console.log('[LeadPipelineStore] checkHasLegacyLeads - organizationId:', organizationId);
      if (!organizationId) {
        console.log('[LeadPipelineStore] checkHasLegacyLeads - No organizationId, skipping');
        return;
      }

      // Verifica se esistono LEAD senza pipeline_id (legacy leads).
      // Deve usare la stessa definizione di lead della vista (clientStore.fetchLeads):
      // stato_cliente che risolve per id a uno stato con is_lead=true. Contare qualsiasi
      // client senza pipeline (clienti attivi, stati legacy testuali) teneva visibile
      // la pipeline "Predefinita (Legacy)" anche con la vista lead vuota.
      const clientStates = await getCachedClientStates(organizationId);
      const leadStateIds = (clientStates || []).filter(s => s.is_lead).map(s => s.id);

      let hasLegacy = false;
      if (leadStateIds.length > 0) {
        const { data, error } = await supabase
          .from('clients')
          .select('id')
          .eq('organization_id', organizationId)
          .is('pipeline_id', null)
          .in('stato_cliente', leadStateIds)
          .limit(1);

        console.log('[LeadPipelineStore] checkHasLegacyLeads - query result:', { data, error, count: data?.length });

        if (error) {
          console.error('[LeadPipelineStore] Error checking legacy leads:', error);
          return;
        }

        hasLegacy = (data?.length ?? 0) > 0;
      }
      console.log('[LeadPipelineStore] checkHasLegacyLeads - setting hasLegacyLeads to:', hasLegacy);
      set({ hasLegacyLeads: hasLegacy });

      // Se non ci sono lead legacy e la pipeline selezionata è 'legacy',
      // seleziona automaticamente la prima pipeline custom di default
      const { selectedPipelineId, pipelines } = get();
      if (!hasLegacy && selectedPipelineId === 'legacy') {
        const customPipelines = pipelines.filter(p => p.id !== 'legacy');
        const defaultPipeline = customPipelines.find(p => p.is_default) || customPipelines[0];
        if (defaultPipeline) {
          console.log('[LeadPipelineStore] checkHasLegacyLeads - No legacy leads, switching from legacy to:', defaultPipeline.id);
          // Usa setSelectedPipeline così la persistenza usa la chiave org-specific corretta
          // (selectedLeadPipelineId_org_<org>) e gli stage vengono caricati automaticamente.
          get().setSelectedPipeline(defaultPipeline.id);
        }
      }
    } catch (error) {
      console.error('[LeadPipelineStore] Error in checkHasLegacyLeads:', error);
    }
  }
}));
